import { useLayoutEffect, useRef } from 'react';
import { useGetAuditTrailEntityTypesQuery } from 'store/api/audit-trail';
import { t } from 'utils/getTranslations';
import { AuditTrailFeed } from './AuditTrailFeed';
import { AuditTrailToolbar } from './AuditTrailToolbar';
import { ChangesCountPill } from './ChangesCountPill';
import { useAuditTrailFeed } from './useAuditTrailFeed';

export function AuditTrailListPage() {
    const { data: entityTypes = [] } = useGetAuditTrailEntityTypesQuery();
    const feed = useAuditTrailFeed();

    const scrollRootRef = useRef<HTMLDivElement>(null);
    const stickyHeaderRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const root = scrollRootRef.current;
        const header = stickyHeaderRef.current;
        if (!root || !header) {
            return;
        }
        const observer = new ResizeObserver(() =>
            root.style.setProperty('--audit-header-h', `${header.offsetHeight}px`)
        );
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={scrollRootRef}
            className="bg-background h-full min-h-0 [scrollbar-gutter:stable] overflow-y-auto px-6 pb-20"
        >
            <div className="mx-auto max-w-[1080px]">
                <div
                    ref={stickyHeaderRef}
                    className="bg-background after:from-bg sticky top-0 z-10 -mx-6 px-6 pt-7 pb-5 after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-3 after:bg-linear-to-b after:to-transparent after:content-['']"
                >
                    <div className="mb-[22px] flex items-end justify-between gap-6">
                        <div>
                            <h1 className="text-primary text-[30px] leading-[38px] font-semibold tracking-[-0.01em]">
                                {t('audit-trail.heading')}
                            </h1>
                            <p className="text-grey-500 mt-1.5 max-w-[560px] text-[13.5px] leading-5">
                                {t('audit-trail.subtitle')}
                            </p>
                        </div>
                        {feed.totalElements !== undefined && (
                            <ChangesCountPill
                                count={feed.totalElements}
                                inTransaction={feed.filters.transactionId !== null}
                            />
                        )}
                    </div>

                    <AuditTrailToolbar
                        filters={feed.filters}
                        entityTypes={entityTypes}
                        onSearchChange={feed.setSearch}
                        onActionChange={feed.setAction}
                        onEntityTypesChange={feed.setEntityTypes}
                        onAuthorIdsChange={feed.setAuthorIds}
                        onChangedAtRangeChange={feed.setChangedAtRange}
                        onHideSystemChange={feed.setHideSystem}
                        onClearTransaction={feed.clearTransactionFilter}
                    />
                </div>

                <AuditTrailFeed
                    entries={feed.entries}
                    groupedByTransaction={feed.filters.transactionId !== null}
                    isInitialLoading={feed.isInitialLoading}
                    isFetchingMore={feed.isFetchingMore}
                    hasMore={feed.hasMore}
                    onLoadMore={feed.loadMore}
                    onTransactionClick={feed.applyTransactionFilter}
                    onRowIdClick={feed.setSearch}
                    onAuthorClick={(authorId) => feed.setAuthorIds([authorId])}
                />
            </div>
        </div>
    );
}
